import { Button, Drawer, Grid, Icon, Tab, Tabs, Tooltip } from '@material-ui/core';
import axios from 'axios';
import Alert from 'components/Alert';
import PermissionHide from 'components/PermissionHide';
import Spin from 'components/Spin';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import { URL_ORBITA } from 'settings';
import TabPanel from 'v2/components/Shared/TabPanel';
import ordersService from 'v2/services/orders';

import Customer from './customer';
import InvoiceItems from './invoiceItems';
import useStyle from './styles';
import Tracking from './tracking';

enum EInvoiceTabs {
  INVOICE,
  VALUES,
}

const Quickview = () => {
  const classes = useStyle();

  const [tabInvoice, setTabInvoice] = useState<EInvoiceTabs>(EInvoiceTabs.INVOICE);

  const [isLoading] = useObservable(() => ordersService.isLoadingQuickview(), []);

  const [invoice] = useObservable(() => ordersService.getInvoice(), []);

  const [order] = useObservable(() => ordersService.getOrder(), []);

  const [open] = useObservable(() => ordersService.getQuickview(), []);

  const changetabInvoice = useCallback((e: React.ChangeEvent<{}>, value: EInvoiceTabs) => {
    setTabInvoice(value);
  }, []);

  const closeQuickView = useCallback(() => ordersService.closeQuickview(), []);

  const generateLabel = useCallback(async () => {
    const response = await axios.get(`${URL_ORBITA}/orders/tags?invoice_id=${invoice.id}&return_url=1`);
    window.open(response.data.data.download_url, '_blank');
  }, [invoice]);

  const openModalRefund = useCallback(() => ordersService.openModalRefund(), []);

  const [onMarkAsSent] = useCallbackObservable(() => {
    return ordersService.markAsSent().pipe(
      tap(() => {
        try {
          toast.success('Entrega marcada como enviada com sucesso!');
        } catch (error) {
          toast.error(error);
        }
      })
    );
  }, []);

  const openModalMarkAsSent = async () => {
    const confirm = await Alert.show({
      message: 'Você tem certeza que deseja marcar essa entrega como enviada?',
      title: 'Atenção',
      confirmation: true,
    });
    if (!confirm) return;
    onMarkAsSent();
  };

  return (
    <Drawer onClose={closeQuickView} anchor='right' open={open} PaperProps={{ className: classes.quickview }}>
      {isLoading && (
        <>
          <Spin />
        </>
      )}
      {!isLoading && invoice && (
        <>
          <div className={classes.header}>
            <Grid container justify='space-between' alignItems='center'>
              <div className={classes.title}>Fatura: {invoice.id}</div>
              <div className={classes.closeButton} onClick={closeQuickView}>
                <Icon style={{ fontSize: '16x', color: '#ccc' }}>clear</Icon>
              </div>
            </Grid>
          </div>
          <div className={classes.container}>
            <Tabs
              value={tabInvoice}
              onChange={changetabInvoice}
              indicatorColor='primary'
              textColor='primary'
              scrollButtons='off'
              classes={{ root: classes.tabsRoot, indicator: classes.tabsIndicator }}
            >
              <Tab
                key={EInvoiceTabs.INVOICE}
                value={EInvoiceTabs.INVOICE}
                disableRipple
                label='Fatura'
                classes={{ root: classes.tabRoot, wrapper: classes.label, selected: classes.tabSelected }}
              />
              <Tab
                key={EInvoiceTabs.VALUES}
                value={EInvoiceTabs.VALUES}
                disableRipple
                label={'Valores'}
                classes={{ root: classes.tabRoot, wrapper: classes.label, selected: classes.tabSelected }}
              />
            </Tabs>
            <TabPanel className={classes.tabPanel} value={tabInvoice} index={EInvoiceTabs.INVOICE}>
              <Grid container xs={12} spacing={2} alignItems={'center'} justify='space-around'>
                <Grid item xs={12} md={4}>
                  <p>Código</p> <span>{invoice.id}</span>
                </Grid>
                <Grid item xs={12} md={4}>
                  <p>Status</p>
                  <span title={invoice?.payment_status?.name}>
                    {invoice.payment_status?.id !== 6 && invoice.payment_status?.id !== 15 && invoice.payment_status.name}
                    {invoice.payment_status?.id === 6 && 'Ag. Reembolso'}
                    {invoice.payment_status?.id === 15 && 'Ag. Pagamento'}
                  </span>
                </Grid>
                <Grid item xs={12} md={4}>
                  {invoice.created_at && (
                    <>
                      <p>Criação</p>
                      <span>{moment(invoice.created_at).format('L')}</span>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  {invoice.received_at && (
                    <>
                      <p>Pagamento</p>
                      <span>{moment(invoice.received_at).format('L')}</span>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  {invoice.credited_at && (
                    <>
                      <p>Crédito</p>
                      <span>{moment(invoice.credited_at).format('L')}</span>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  {invoice.expired_at && (
                    <>
                      <p>Vencimento</p>
                      <span>{moment(invoice.expired_at).format('L')}</span>
                    </>
                  )}
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel className={classes.tabPanel} value={tabInvoice} index={EInvoiceTabs.VALUES}>
              <Grid container xs={12} spacing={2} alignItems={'center'} justify='space-around'>
                <Grid item xs={12} md={4}>
                  <p>Venda</p>
                  <>
                    <PermissionHide inverse={1} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <FormattedNumber format='BRL' value={invoice.sale_value} />
                    </PermissionHide>
                    <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <em>oculto</em>
                    </PermissionHide>
                  </>
                </Grid>
                <Grid item xs={12} md={4}>
                  <p>Taxa</p>
                  <>
                    <PermissionHide inverse={1} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <FormattedNumber format='BRL' value={invoice.rate_amount} />
                    </PermissionHide>
                    <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <em>oculto</em>
                    </PermissionHide>
                  </>
                </Grid>
                <Grid item xs={12} md={4}>
                  <p>Comissão</p>
                  <>
                    <PermissionHide inverse={1} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <FormattedNumber format='BRL' value={invoice.commission_amount} />
                    </PermissionHide>
                    <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <em>oculto</em>
                    </PermissionHide>
                  </>
                </Grid>
                <Grid item xs={12} md={4}>
                  <p>Parceiro</p>
                  <>
                    <PermissionHide inverse={1} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <FormattedNumber format='BRL' value={invoice.partner_amount} />
                    </PermissionHide>
                    <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <em>oculto</em>
                    </PermissionHide>
                  </>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Tooltip placement='top' title='A antecipação é descontada diretamente no extrato financeiro (valor apenas para conhecimento)'>
                    <p>
                      Antecipação <Icon style={{ fontSize: 14, display: 'inline-block' }}>info</Icon>
                    </p>
                  </Tooltip>
                  <>
                    <PermissionHide inverse={1} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <FormattedNumber format='BRL' value={invoice.anticipation_amount} />
                    </PermissionHide>
                    <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <em>oculto</em>
                    </PermissionHide>
                  </>
                </Grid>
                <Grid item xs={12} md={4}>
                  <p>Outros</p>
                  <>
                    <PermissionHide inverse={1} action='myeduzz:minhas-entregas:ocultar-valores'>
                      {/* <CustomMaskedInput mask='money' value={invoice.others_values} /> */}
                      <FormattedNumber format='BRL' value={invoice.others_values} />
                    </PermissionHide>
                    <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
                      <em>oculto</em>
                    </PermissionHide>
                  </>
                </Grid>
              </Grid>
            </TabPanel>
            <div className='m24' />
            <Customer />
            <div className='m24' />
            <InvoiceItems />
            <div className='m24' />
            <Tracking />
            <div className='m24' />
            <Grid container xs={12} spacing={2}>
              <Grid item xs={12} md={6}>
                <PermissionHide action='myeduzz:minhas-entregas:editar'>
                  <Button variant='text' color='default' onClick={generateLabel} style={{ marginRight: '1rem' }}>
                    Gerar Etiqueta
                  </Button>
                </PermissionHide>
              </Grid>
              <Grid item xs={12} md={6}>
                <PermissionHide action='myeduzz:minhas-entregas:reembolsar'>
                  {invoice.payment_status?.id === 3 && (
                    <Button color='default' onClick={openModalRefund}>
                      Reembolsar fatura
                    </Button>
                  )}
                </PermissionHide>
              </Grid>
              <Grid item xs={12}>
                <PermissionHide action='myeduzz:minhas-entregas:editar'>
                  {order?.status === 'Aguardando Envio' && (
                    <Button color='secondary' onClick={openModalMarkAsSent}>
                      Marcar como enviado
                    </Button>
                  )}
                </PermissionHide>
              </Grid>
            </Grid>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default Quickview;
